import { Box, Stack } from "@mui/material";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useMemo } from "react";

import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import {
  SortOrder,
  WithdrawalListOrderBy,
  WithdrawalRequestStatus,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { TableSearchInput } from "Projects/Components/TableSearchInput";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlDropdown } from "Shared/Inputs/BlDropdown";
import type { BlOption } from "Shared/Inputs/BlOption";
import { useWithdrawalApprovalsQuery } from "WithdrawalApprovals/Api/Queries/useWithdrawalApprovalsQuery";
import {
  useWithdrawalApprovalsTableColumns,
  WITHDRAWAL_SORTABLE_COLUMNS,
} from "WithdrawalApprovals/Components/useWithdrawalApprovalsTableColumns";

const SORT_COLUMN_MAP: Record<string, WithdrawalListOrderBy> = {
  actorName: WithdrawalListOrderBy.ActorName,
  amount: WithdrawalListOrderBy.Amount,
  status: WithdrawalListOrderBy.Status,
  requestedAt: WithdrawalListOrderBy.RequestedAt,
};

export const WithdrawalApprovalsTable = (): React.ReactNode => {
  const { t } = useResource();

  const [selectedStatus, setSelectedStatus] = useQueryState(
    "status",
    parseAsStringEnum<WithdrawalRequestStatus>(
      Object.values(WithdrawalRequestStatus),
    ),
  );

  const {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    resetPage,
    sorting,
    onSortingChange,
  } = useTableSearchParams({
    validSortIds: [...WITHDRAWAL_SORTABLE_COLUMNS],
  });

  const orderBy = sorting[0]?.id ? SORT_COLUMN_MAP[sorting[0].id] : undefined;
  const sortOrder = sorting[0]?.desc
    ? SortOrder.Descending
    : SortOrder.Ascending;

  const { data, isLoading } = useWithdrawalApprovalsQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    searchQuery: debouncedSearch || undefined,
    status: selectedStatus,
    orderBy,
    sortOrder,
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const statusOptions = useMemo(
    (): BlOption[] => [
      { value: "", label: t(Resources.Common.AllStatuses) },
      {
        value: WithdrawalRequestStatus.Pending,
        label: t(Resources.WithdrawalApprovals.StatusPending),
      },
      {
        value: WithdrawalRequestStatus.Approved,
        label: t(Resources.WithdrawalApprovals.StatusApproved),
      },
      {
        value: WithdrawalRequestStatus.Rejected,
        label: t(Resources.WithdrawalApprovals.StatusRejected),
      },
      {
        value: WithdrawalRequestStatus.Expired,
        label: t(Resources.WithdrawalApprovals.StatusExpired),
      },
    ],
    [t],
  );

  const columns = useWithdrawalApprovalsTableColumns();

  const table = useReactTable({
    data: items,
    columns,
    getRowId: (row) => row.withdrawalRequestID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize }, sorting },
    onSortingChange,
    onPaginationChange,
  });

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={Spacing.M}
        sx={{ marginBottom: Spacing.M }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <TableSearchInput
            name="withdrawal-search"
            value={search}
            onChange={onSearchChange}
            placeholder={t(Resources.WithdrawalApprovals.SearchPlaceholder)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <BlDropdown
            name="status-filter"
            options={statusOptions}
            value={selectedStatus ?? ""}
            onChange={(value) => {
              setSelectedStatus(
                value ? (value as WithdrawalRequestStatus) : null,
              );
              resetPage();
            }}
            placeholder={t(Resources.WithdrawalApprovals.Status)}
          />
        </Box>
      </Stack>
      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={totalCount}
      />
    </Box>
  );
};
